import React, { useCallback } from 'react';
import { styled } from '@compiled/react';
import { Field as AkField, ErrorMessage as AkErrorMessage, HelperMessage } from '@atlaskit/form';
import ClockIcon from '@atlaskit/icon/core/clock';
import { AsyncCreatableSelect as AkSelect } from '@atlaskit/select';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { useIntl } from '@atlassian/jira-intl';
import { useCommonPageContext } from '@atlassian/jira-servicedesk-common-page-context';
import { NAME_FIELD } from '../../../../../../common/ui/constants.tsx';
import { HasPermission } from '../../../../../../common/ui/has-permission/index.tsx';
import { useSlaFieldsService } from '../../../../../../services/sla-fields/index.tsx';
import type { Fields } from '../../../../../../services/sla-fields/types.tsx';
import { EMPTY_NAME } from './constants';
import messages from './messages';
import { validateName, debounce } from './utils';

type Props = {
	canManageFields: boolean;
	name?: string;
};

const fetchWithDebounce = debounce(async (fetch, query, resolve) => {
	const options: Fields | undefined = await fetch(query);
	const filteredOption = (options || []).filter((option) =>
		option.label.toLowerCase().includes(query.toLowerCase()),
	);
	resolve(filteredOption);
});

const NameField = ({ name, canManageFields }: Props) => {
	const { baseUrl } = useCommonPageContext();
	// @ts-expect-error(TS2322 baseUrl, projectId or projectKey can be undefined, if usePageContext() returns null. Please handle undefined value in your code) - Argument of type 'string | undefined' is not assignable to parameter of type 'string'.
	const [, { fetch }] = useSlaFieldsService(baseUrl);

	const { formatMessage } = useIntl();

	const fetchOnInputChange = useCallback(
		(query: string, resolve: (arg1: Fields) => void) => {
			fetchWithDebounce(fetch, query, resolve);
		},
		[fetch],
	);

	const defaultValue = { value: name, label: name };

	return (
		<HeadingContainer>
			<IconContainer>
				<ClockIcon
					LEGACY_size="large"
					LEGACY_primaryColor={token('color.icon', colors.N500)}
					label=""
					color="currentColor"
				/>
			</IconContainer>
			<FieldWrapper>
				<AkField
					name={NAME_FIELD}
					// @ts-expect-error - TS2322 - Type '(fieldValue: Field | null) => string | undefined' is not assignable to type '(value: Field | null | undefined, formState: Object, fieldState: Meta) => string | void | Promise<string | void>'.
					validate={validateName}
					isRequired
					// @ts-expect-error - TS2322 - Type '{ value: string | undefined; label: string | undefined; }' is not assignable to type 'Field | ((currentDefaultValue?: Field | null | undefined) => Field | null) | null | undefined'.
					defaultValue={defaultValue}
				>
					{({ fieldProps, error }) => (
						<>
							<div data-testid="servicedesk-sla-settings.ui.metrics.metric.editable.form.name.name-dropdown">
								<AkSelect
									autoFocus
									formatCreateLabel={(newName: string) =>
										`${formatMessage(messages.createNew, { newName })}`
									}
									aria-label={formatMessage(messages.newName)}
									placeholder={formatMessage(messages.placeHolder)}
									loadOptions={fetchOnInputChange}
									{...fieldProps}
									defaultOptions
									// @ts-expect-error - TS2367 - This condition will always return 'true' since the types 'number | undefined' and 'string' have no overlap.
									isClearable={fieldProps.value?.value !== ''}
								/>
							</div>
							<HasPermission hasPermission={!canManageFields}>
								<HelperMessage>{formatMessage(messages.chooseMetricName)}</HelperMessage>
							</HasPermission>
							{error === EMPTY_NAME && (
								<AkErrorMessage>{formatMessage(messages.emptyMetricName)}</AkErrorMessage>
							)}
						</>
					)}
				</AkField>
			</FieldWrapper>
		</HeadingContainer>
	);
};

export const Name = NameField;

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const HeadingContainer = styled.div({
	display: 'flex',
	alignItems: 'center',
	minHeight: '64px',
	marginBottom: token('space.100', '8px'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const IconContainer = styled.div({
	margin: `${token('space.100', '8px')} ${token('space.100', '8px')} auto ${token(
		'space.negative.050',
		'-4px',
	)}`,
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FieldWrapper = styled.div({
	flex: 1,
	margin: `${token('space.negative.050', '-4px')} 0 auto 0`,
});
